import { Placeholder } from '@tiptap/extensions';
import { type Editor, EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  RemoveFormatting,
  Strikethrough,
  Underline,
  Unlink,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { normalizeRichText, toRichTextHtml } from '@/util/util_richText';

// A rich-text form field built on Tiptap — the same editor /portal uses for its
// rich-text notes (see AutosaveRichTextInput there), minus the autosave
// plumbing: this one is a plain controlled input, reporting every change to the
// form that owns it.
//
// The value is HTML in a string, sanitized with DOMPurify on the way out, so a
// field can be switched from a Textarea to this one without a schema change.
// Render it back with `<RichText>`.

interface RichTextFieldProps {
  /** The stored value: HTML, or legacy plain text (newlines become line breaks). */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Fired when focus leaves the whole widget — not when it moves editor ↔ toolbar. */
  onBlur?: () => void;
  /** Escape pressed in the editor. Used by the autosave wrapper to drop the draft. */
  onEscape?: () => void;
  /** Extra classes for the bordered container (the autosave dirty cue). */
  className?: string;
}

// Links keep the same safe attributes whether written by the editor or pasted, and
// DOMPurify is told to preserve `target`/`rel` (it strips `target` by default).
const linkHTMLAttributes = { rel: 'noopener noreferrer nofollow', target: '_blank' };

/** Prefix a bare URL (or leave mailto:/anchors/schemes untouched) so links always resolve. */
function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (trimmed === '') return '';
  if (/^(https?:|mailto:|#|\/)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function RichTextField({
  value,
  onChange,
  placeholder,
  onBlur,
  onEscape,
  className,
}: RichTextFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
          protocols: ['http', 'https', 'mailto'],
          HTMLAttributes: linkHTMLAttributes,
        },
        // A bio needs emphasis and lists, not a document outline.
        codeBlock: false,
        horizontalRule: false,
        heading: false,
      }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: toRichTextHtml(value),
    onUpdate: ({ editor }) => onChange(normalizeRichText(editor.getHTML())),
  });

  // Pull in changes made outside the editor (a form reset, a loaded document)
  // without clobbering what is being typed — the guard makes the round trip of
  // our own onUpdate a no-op.
  useEffect(() => {
    if (!editor) return;
    if (normalizeRichText(editor.getHTML()) !== value) {
      editor.commands.setContent(toRichTextHtml(value), { emitUpdate: false });
    }
  }, [editor, value]);

  function openLinkPopover() {
    if (!editor) return;
    setLinkDraft(editor.getAttributes('link').href ?? '');
    setLinkPopoverOpen(true);
  }

  function applyLink() {
    if (!editor) return;
    const href = normalizeUrl(linkDraft);
    if (href === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else if (editor.state.selection.empty && !editor.isActive('link')) {
      // No selection: drop the URL in as its own linked text.
      editor
        .chain()
        .focus()
        .insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    setLinkPopoverOpen(false);
  }

  return (
    <div
      ref={containerRef}
      onBlur={(e) => {
        // Focus moving within the widget (editor ↔ toolbar ↔ link popover) is not a real blur.
        const next = e.relatedTarget as HTMLElement | null;
        if (containerRef.current && next && containerRef.current.contains(next)) return;
        setFocused(false);
        setLinkPopoverOpen(false);
        onBlur?.();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Escape') return;
        // While the link popover is open Escape belongs to it — its own handler
        // closes it, and this still sees the pre-close state to stay out of the way.
        if (linkPopoverOpen) return;
        e.preventDefault();
        onEscape?.();
        editor?.commands.blur();
      }}
      className={cn(
        'tw:relative tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-white tw:shadow-sm',
        'tw:transition-shadow tw:duration-150 tw:ease-in-out',
        focused ? 'tw:ring-4 tw:ring-blue-500/40' : '',

        // ProseMirror editor surface — Tailwind's preflight resets lists, so restyle them here.
        'tw:[&_.ProseMirror]:min-h-40 tw:[&_.ProseMirror]:px-2 tw:[&_.ProseMirror]:py-1.5',
        'tw:[&_.ProseMirror]:outline-none',
        'tw:[&_.ProseMirror_p]:my-1',
        'tw:[&_.ProseMirror_ul]:my-1 tw:[&_.ProseMirror_ul]:list-disc tw:[&_.ProseMirror_ul]:pl-5',
        'tw:[&_.ProseMirror_ol]:my-1 tw:[&_.ProseMirror_ol]:list-decimal tw:[&_.ProseMirror_ol]:pl-5',
        'tw:[&_.ProseMirror_a]:text-blue-600 tw:[&_.ProseMirror_a]:underline',
        'tw:[&_.ProseMirror_strong]:font-semibold',
        // Placeholder text for the empty document (the class sits on the empty first child).
        'tw:[&_.ProseMirror_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
        'tw:[&_.ProseMirror_.is-editor-empty:first-child::before]:text-muted-foreground',
        'tw:[&_.ProseMirror_.is-editor-empty:first-child::before]:float-left',
        'tw:[&_.ProseMirror_.is-editor-empty:first-child::before]:h-0',
        'tw:[&_.ProseMirror_.is-editor-empty:first-child::before]:pointer-events-none',
        className,
      )}
    >
      {editor && <Toolbar editor={editor} onLinkClick={openLinkPopover} />}

      <EditorContent editor={editor} onFocus={() => setFocused(true)} />

      {linkPopoverOpen && (
        <div className='tw:absolute tw:top-9 tw:left-2 tw:z-50 tw:flex tw:items-center tw:gap-1 tw:rounded tw:border tw:bg-white tw:p-1 tw:shadow-md'>
          <input
            autoFocus
            type='text'
            value={linkDraft}
            placeholder='https://…'
            className='tw:w-56 tw:rounded tw:border tw:border-input tw:px-1.5 tw:py-0.5 tw:text-xs tw:outline-none'
            onChange={(e) => setLinkDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setLinkPopoverOpen(false);
                editor?.commands.focus();
              }
            }}
          />
          <button
            type='button'
            className='tw:rounded tw:bg-primary tw:px-2 tw:py-0.5 tw:text-xs tw:text-primary-foreground'
            onMouseDown={(e) => e.preventDefault()}
            onClick={applyLink}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}

function Toolbar({ editor, onLinkClick }: { editor: Editor; onLinkClick: () => void }) {
  // Recompute the active flags on every selection or document change so the
  // buttons highlight what the cursor is sitting in.
  const state = useEditorState({
    editor,
    selector: ({ editor }) => ({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      strike: editor.isActive('strike'),
      bulletList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      link: editor.isActive('link'),
    }),
  });

  return (
    <div className='tw:flex tw:flex-wrap tw:items-center tw:gap-0.5 tw:border-b tw:border-input tw:px-1 tw:py-1'>
      <ToolbarButton active={state.bold} title='Bold' onClick={() => editor.chain().focus().toggleBold().run()}>
        <Bold className='tw:size-3.5' />
      </ToolbarButton>
      <ToolbarButton
        active={state.italic}
        title='Italic'
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className='tw:size-3.5' />
      </ToolbarButton>
      <ToolbarButton
        active={state.underline}
        title='Underline'
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <Underline className='tw:size-3.5' />
      </ToolbarButton>
      <ToolbarButton
        active={state.strike}
        title='Strikethrough'
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className='tw:size-3.5' />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        active={state.bulletList}
        title='Bullet list'
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className='tw:size-3.5' />
      </ToolbarButton>
      <ToolbarButton
        active={state.orderedList}
        title='Numbered list'
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className='tw:size-3.5' />
      </ToolbarButton>

      <Divider />

      <ToolbarButton active={state.link} title='Link' onClick={onLinkClick}>
        <LinkIcon className='tw:size-3.5' />
      </ToolbarButton>
      {state.link && (
        <ToolbarButton
          active={false}
          title='Remove link'
          onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
        >
          <Unlink className='tw:size-3.5' />
        </ToolbarButton>
      )}

      <Divider />

      <ToolbarButton
        active={false}
        title='Clear formatting'
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
      >
        <RemoveFormatting className='tw:size-3.5' />
      </ToolbarButton>
    </div>
  );
}

function Divider() {
  return <span className='tw:mx-0.5 tw:h-4 tw:w-px tw:bg-input' />;
}

function ToolbarButton({
  active,
  title,
  onClick,
  children,
}: {
  active: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type='button'
      title={title}
      // preventDefault keeps the editor selection/focus while the button is pressed.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'tw:flex tw:size-6 tw:items-center tw:justify-center tw:rounded tw:text-muted-foreground tw:hover:bg-muted',
        active ? 'tw:bg-primary/10 tw:text-primary' : '',
      )}
    >
      {children}
    </button>
  );
}
